import React, { useState } from 'react';
import axios from 'axios';
import { Copy, Download, FileText, Loader2, Sparkles, Table2 } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader, describeError } from '@/components/primitives';
import { API, authHeaders, copyText, downloadFile } from '@/pages/dashboard/api';
import '@/styles/reports.css';

const today = () => new Date().toISOString().slice(0, 10);

const REPORTS = [
  {
    id: 'weekly', icon: FileText, title: 'Weekly threat intelligence report', format: 'PDF',
    description: 'Board-ready summary of the last 7 days: posture, recommended actions, the threat register and priority briefs.',
    path: '/dashboard/weekly-report', file: () => `IntelliSecure_Weekly_Threat_Report_${today()}.pdf`, action: 'Download PDF',
  },
  {
    id: 'threats', icon: Table2, title: 'Threat export', format: 'CSV',
    description: 'Every brief matched to your organization, with severity, actor, CVEs and dates, for your own reporting or GRC tools.',
    path: '/dashboard/export/threats', file: () => `intellisecure_threats_${today()}.csv`, action: 'Download CSV',
  },
  {
    id: 'iocs', icon: Table2, title: 'Indicator export', format: 'CSV',
    description: 'All indicators from your briefs with type and source threat, ready to load into a SIEM, EDR or firewall.',
    path: '/dashboard/export/iocs', file: () => `intellisecure_iocs_${today()}.csv`, action: 'Download CSV',
  },
];

function ExecutiveSummary() {
  const [state, setState] = useState({ loading: false, brief: null });
  const generate = async () => {
    setState({ loading: true, brief: null });
    try {
      const res = await axios.post(`${API}/dashboard/executive-brief`, {}, { headers: authHeaders() });
      setState({ loading: false, brief: res.data });
    } catch (err) {
      setState({ loading: false, brief: null });
      toast.error("Couldn't write the summary", { description: describeError(err) });
    }
  };
  const text = state.brief?.brief;
  return (
    <section className="rp-panel" aria-labelledby="rp-exec">
      <div className="rp-panel-head">
        <div>
          <h2 id="rp-exec" className="t-heading"><Sparkles aria-hidden="true" className="rp-ic" /> Executive summary</h2>
          <p className="rp-desc">A plain-language paragraph for leadership, written from your current briefs. Review it before you send it on.</p>
        </div>
        <button type="button" className="is-btn is-btn--primary is-btn--sm" onClick={generate} disabled={state.loading}>
          {state.loading ? <Loader2 className="rp-ic rp-spin" aria-hidden="true" /> : <Sparkles className="rp-ic" aria-hidden="true" />}
          {text ? 'Write again' : 'Write summary'}
        </button>
      </div>
      {state.loading && <p className="rp-status" role="status">Writing from your latest briefs…</p>}
      {text && (
        <>
          {state.brief.stats && (
            <dl className="rp-stats">
              <div><dt>Critical</dt><dd>{state.brief.stats.critical}</dd></div>
              <div><dt>High</dt><dd>{state.brief.stats.high}</dd></div>
              <div><dt>Briefs covered</dt><dd>{state.brief.stats.total}</dd></div>
            </dl>
          )}
          <div className="rp-brief">{text}</div>
          <div className="rp-actions">
            <button type="button" className="is-btn is-btn--secondary is-btn--sm" onClick={async () => (await copyText(text) ? toast.success('Summary copied') : toast.error("Couldn't copy to the clipboard"))}>
              <Copy className="rp-ic" aria-hidden="true" /> Copy
            </button>
          </div>
        </>
      )}
    </section>
  );
}

/** Everything a customer can take out of the platform, in one place. */
export default function Reports() {
  const [busy, setBusy] = useState(null);
  const download = async (r) => {
    setBusy(r.id);
    try {
      const name = await downloadFile(r.path, r.file());
      toast.success(`${r.title} downloaded`, { description: name });
    } catch (err) {
      toast.error(`Couldn't download the ${r.title.toLowerCase()}`, { description: describeError(err) });
    } finally {
      setBusy(null);
    }
  };
  return (
    <div className="pg" data-testid="reports-page">
      <PageHeader title="Reports" subtitle="Reports and exports for leadership, auditors and your own tools. Generated on demand from your current data." />
      <section className="rp-panel" aria-labelledby="rp-files">
        <h2 id="rp-files" className="sr-only">Reports and exports</h2>
        <ul className="rp-list">
          {REPORTS.map((r) => {
            const Icon = r.icon;
            return (
              <li key={r.id} className="rp-row">
                <span className="rp-row-icon" aria-hidden="true"><Icon className="rp-ic" /></span>
                <div className="rp-row-main">
                  <div className="rp-row-title">{r.title} <span className="rp-format">{r.format}</span></div>
                  <p className="rp-desc">{r.description}</p>
                </div>
                <button type="button" className="is-btn is-btn--secondary is-btn--sm" onClick={() => download(r)} disabled={busy !== null}>
                  {busy === r.id ? <Loader2 className="rp-ic rp-spin" aria-hidden="true" /> : <Download className="rp-ic" aria-hidden="true" />}
                  {busy === r.id ? 'Preparing…' : r.action}
                </button>
              </li>
            );
          })}
        </ul>
      </section>
      <ExecutiveSummary />
    </div>
  );
}
